import { Injectable } from '@angular/core';
import { ConsolidatedError, ExcelRow, ValidationError } from '../doqfy-components/bulk-upload-pdf-estamp/bulk-upload.interfaces';

@Injectable({ providedIn: 'root' })
export class EsignValidatorService {

  readonly requiredColumns: string[] = [
    'referance_id',
    'branch_code',
    'name',
    'email',
    'contact_number',
    'sign_position',
    'method',
    'pages',
    'user_type',
    'file_name',
  ];

  readonly validSignPositions: string[] = [
    'TOP_LEFT', 'TOP_CENTRE', 'TOP_CENTER', 'TOP_RIGHT',
    'MIDDLE_LEFT', 'MIDDLE_RIGHT',
    'BOTTOM_LEFT', 'BOTTOM_CENTRE', 'BOTTOM_CENTER', 'BOTTOM_RIGHT',
    'BOTTOM_LEFT_MIDDLE', 'BOTTOM_RIGHT_MIDDLE',
  ];

  validate(
    rows: ExcelRow[],
    userBranches: string[],
    refIdFileNameValidation: boolean = false
  ): EsignValidationResult {
    const issueList: ValidationError[]          = [];
    const branchErrors: ValidationError[]       = [];
    const refDuplicates: ValidationError[]      = [];
    const fileNameDuplicates: ValidationError[] = [];
    const signPositionErrors: ValidationError[] = [];
    const refIdFileNameErrors: ValidationError[] = [];
    const rowsWithIssuesSet = new Set<ExcelRow>();

    // 1. Branch code check
    rows.forEach((row, index) => {
      if (
        row.branch_code &&
        !userBranches.map(b => b.toLowerCase()).includes(row.branch_code.toLowerCase())
      ) {
        branchErrors.push({
          rowNumber: index + 2,
          error: `Branch Code "${row.branch_code}" does not belong to your account.`,
        });
        rowsWithIssuesSet.add(row);
      }
    });

    // 2. Duplicate reference_id check
    // Same ref_id is allowed across multiple rows (multiple signatories for same file).
    // Flag only if same ref_id is used for a DIFFERENT file_name.
    const refIdFileMap = new Map<string, string>();
    rows.forEach((row, index) => {
      if (row.referance_id) {
        const key      = row.referance_id.toString().toLowerCase().trim();
        const fileLower = row.file_name?.toString().toLowerCase().trim() ?? '';
        if (refIdFileMap.has(key)) {
          if (refIdFileMap.get(key) !== fileLower) {
            refDuplicates.push({
              rowNumber: index + 2,
              error: `Duplicate reference_id "${row.referance_id}" used for a different file.`,
            });
            rowsWithIssuesSet.add(row);
          }
        } else {
          refIdFileMap.set(key, fileLower);
        }
      }
    });

    // 3. Same file_name must NOT appear under a different reference_id.
    // Build a map: file_name -> first referance_id that claimed it.
    // After _processRawRows inheritance, every row has referance_id filled.
    const fileToRefId = new Map<string, string>();
    rows.forEach((row, index) => {
      const fileName = row.file_name?.toString().trim().toLowerCase() ?? '';
      const refId    = row.referance_id?.toString().trim().toLowerCase() ?? '';
      if (!fileName || !refId) return;

      if (fileToRefId.has(fileName)) {
        if (fileToRefId.get(fileName) !== refId) {
          const errMsg = `File "${row.file_name}" is already used under a different reference ID. Each file must belong to only one reference ID.`;
          fileNameDuplicates.push({ rowNumber: index + 2, error: errMsg });
          rowsWithIssuesSet.add(row);
        }
      } else {
        fileToRefId.set(fileName, refId);
      }
    });

    // 4. Field-level validation per row
    rows.forEach((row, index) => {
      const errors: string[] = [];

      const name = this._validateName(row.name);
      if (name) errors.push(name);

      const email = this._validateEmail(row.email);
      if (email) errors.push(email);

      const contact = this._validateContactNumber(row.contact_number);
      if (contact) errors.push(contact);

      const sp = this._validateSignPosition(row.sign_position);
      if (sp) errors.push(sp);

      const method = this._validateMethod(row.method);
      if (method) errors.push(method);

      const pages = this._validatePages(row.pages);
      if (pages) errors.push(pages);

      const ut = this._validateUserType(row.user_type);
      if (ut) errors.push(ut);

      const fn = this._validateFileName(row.file_name);
      if (fn) errors.push(fn);

      if (errors.length > 0) {
        issueList.push({ rowNumber: index + 2, error: errors.join(' ') });
        rowsWithIssuesSet.add(row);
      }
    });

    // 5. Duplicate sign_position within the same PDF file.
    // Group rows by file_name so that the same sign position used in different
    // PDF files is allowed — only duplicate positions within the same file are flagged.
    const fileGroups = new Map<string, { row: ExcelRow; index: number }[]>();
    rows.forEach((row, index) => {
      const fileName = row.file_name?.toString().trim().toLowerCase() ?? '';
      if (!fileName) return;
      if (!fileGroups.has(fileName)) fileGroups.set(fileName, []);
      fileGroups.get(fileName)!.push({ row, index });
    });

    fileGroups.forEach((entries) => {
      const seenPositions = new Map<string, number>(); // position -> first rowNumber
      entries.forEach(({ row, index }) => {
        const pos = row.sign_position?.toString().trim().toUpperCase() ?? '';
        if (!pos) return;
        if (seenPositions.has(pos)) {
          const firstRow = seenPositions.get(pos)!;
          const errMsg   = `Duplicate sign_position "${pos}" for file "${row.file_name}" — already used at row ${firstRow}. Each signatory must have a unique sign position within the same file.`;
          signPositionErrors.push({ rowNumber: index + 2, error: errMsg });
          rowsWithIssuesSet.add(row);
        } else {
          seenPositions.set(pos, index + 2);
        }
      });
    });

    // 6. Reference ID must exactly match filename without .pdf extension (case-sensitive)
    if (refIdFileNameValidation) {
      const checkedFiles = new Set<string>();
      rows.forEach((row, index) => {
        const fileName = row.file_name?.toString().trim() ?? '';
        if (!fileName || checkedFiles.has(fileName.toLowerCase())) return;
        checkedFiles.add(fileName.toLowerCase());

        const refId = row.referance_id?.toString().trim() ?? '';
        const fileNameWithoutExt = fileName.replace(/\.pdf$/i, '');

        if (refId !== fileNameWithoutExt) {
          const errMsg = `Reference ID "${refId || '(empty)'}" must exactly match the filename without extension "${fileNameWithoutExt}" (case-sensitive).`;
          refIdFileNameErrors.push({ rowNumber: index + 2, error: errMsg });
          rowsWithIssuesSet.add(row);
        }
      });
    }

    // 7. Consolidate all errors
    const consolidatedErrors = this._consolidate([
      ...issueList,
      ...branchErrors,
      ...refDuplicates,
      ...fileNameDuplicates,
      ...signPositionErrors,
      ...refIdFileNameErrors,
    ]);

    // 8. Valid row count
    const allErrorRows = new Set<number>([
      ...issueList,
      ...branchErrors,
      ...refDuplicates,
      ...fileNameDuplicates,
      ...signPositionErrors,
      ...refIdFileNameErrors,
    ].map(e => e.rowNumber!));

    const validRowCount = rows.filter((_, i) => !allErrorRows.has(i + 2)).length;

    return {
      isValid: consolidatedErrors.length === 0,
      consolidatedErrors,
      rowsWithIssues: Array.from(rowsWithIssuesSet),
      validRowCount,
      fileNameDuplicateErrors: fileNameDuplicates,
    };
  }

  private _consolidate(errors: ValidationError[]): ConsolidatedError[] {
    const seen   = new Set<string>();
    const result: ConsolidatedError[] = [];
    errors.forEach(e => {
      const text = `Row ${e.rowNumber} : ${e.error}`;
      if (!seen.has(text)) {
        seen.add(text);
        result.push({ displayText: text, originalItem: e });
      }
    });
    return result;
  }

  private _validateName(v: any): string | null {
    const fmt = /^[a-zA-Z0-9\s]+$/;
    if (!v || typeof v !== 'string') return 'Invalid or missing Name.';
    if (v.length > 50) return 'Name should not exceed 50 characters.';
    if (!fmt.test(v)) return 'Name should not contain special characters.';
    return null;
  }

  private _validateEmail(v: any): string | null {
    const fmt = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!v || typeof v !== 'string') return 'Invalid or missing Email.';
    if (!fmt.test(v)) return 'Invalid email format.';
    return null;
  }

  private _validateContactNumber(v: any): string | null {
    if (!v) return null;
    const str = v.toString().trim();
    const fmt = /^[6-9]\d{9}$/;
    if (!fmt.test(str)) {
      return 'Contact number must be exactly 10 digits and start with 6, 7, 8, or 9.';
    }
    return null;
  }

  private _validateSignPosition(v: any): string | null {
    if (!v || typeof v !== 'string') return 'Invalid or missing Sign Position.';
    if (!this.validSignPositions.includes(v))
      return `Invalid Sign Position "${v}". Must be one of the valid positions in ALL_CAPS (e.g. TOP_LEFT, BOTTOM_RIGHT).`;
    return null;
  }

  private _validateMethod(v: any): string | null {
    if (!v || typeof v !== 'string') return 'Invalid or missing Method.';
    return null;
  }

  private _validatePages(v: any): string | null {
    if (!v && v !== 0) return 'Invalid or missing Pages.';
    return null;
  }

  private _validateUserType(v: any): string | null {
    if (!v || typeof v !== 'string') return 'Invalid or missing User Type.';
    return null;
  }

  private _validateFileName(v: any): string | null {
    if (!v) return 'file_name is required.';
    if (!v.toString().toLowerCase().endsWith('.pdf')) return `file_name "${v}" must be a PDF file.`;
    return null;
  }
}

export interface EsignValidationResult {
  isValid: boolean;
  consolidatedErrors: ConsolidatedError[];
  rowsWithIssues: ExcelRow[];
  validRowCount: number;
  fileNameDuplicateErrors: ValidationError[];
}